/**
 * AppLayout — §14.3 "(app)" layout.
 * Full app shell: collapsible sidebar + topbar + breadcrumbs + scrollable content area.
 * Used by all internal-staff routes (dashboard, batches, invoices, etc.).
 *
 * M1.4 additions:
 *   - <Breadcrumbs /> mounted between topbar and content.
 *   - document.title updated from route handle.title (falls back to "NIRC SCO Platform").
 */

import { useEffect } from 'react';
import { Outlet, useMatches } from 'react-router-dom';

import AppSidebar from '@/components/layout/AppSidebar';
import AppTopbar from '@/components/layout/AppTopbar';
import Breadcrumbs from '@/components/layout/Breadcrumbs';
import { CommandPalette } from '@/components/layout/CommandPalette';
import CreatedByCredit from '@/components/layout/CreatedByCredit';
import RequireUserType from '@/components/auth/RequireUserType';
import { useUIStore } from '@/stores/ui-store';
import { cn } from '@/lib/utils';

const SITE_NAME = 'NIRC SCO Platform';

interface RouteHandle {
  title?: string;
  crumb?: unknown;
}

export default function AppLayout() {
  const sidebarCollapsed = useUIStore((s) => s.sidebarCollapsed);
  const matches = useMatches();

  // Set document.title from the deepest matched route that carries handle.title
  useEffect(() => {
    const deepest = [...matches]
      .reverse()
      .find((m) => typeof (m.handle as RouteHandle | null)?.title === 'string');

    const title = (deepest?.handle as RouteHandle | undefined)?.title;
    document.title = title ? `${title} · ${SITE_NAME}` : SITE_NAME;
  }, [matches]);

  return (
    // Defense-in-depth: external (faculty/vendor) users cannot load the (app)
    // shell by URL — they are bounced to /portal. Backend RBAC remains the real
    // security boundary; this is UX + belt-and-suspenders only.
    <RequireUserType allow="internal">
      {/* ⌘K / topbar-search command palette — available on every internal page */}
      <CommandPalette />
      <div className="flex h-screen overflow-hidden bg-background">
        {/* Sidebar — fixed width, collapsible */}
        <AppSidebar />

        {/* Main content column */}
        <div
          className={cn(
            'flex flex-col flex-1 overflow-hidden transition-all duration-200',
            sidebarCollapsed ? 'ml-0' : 'ml-0'
          )}
        >
          {/* Topbar */}
          <AppTopbar />

          {/* Breadcrumbs — hidden on single-segment routes (see Breadcrumbs.tsx) */}
          <Breadcrumbs />

          {/* Scrollable content */}
          <main className="flex-1 overflow-y-auto p-8">
            <div className="max-w-7xl mx-auto">
              <Outlet />
            </div>
          </main>

          {/* Persistent footer credit */}
          <footer className="shrink-0 border-t border-border px-8 py-2">
            <CreatedByCredit />
          </footer>
        </div>
      </div>
    </RequireUserType>
  );
}
